import { format, parseISO } from 'date-fns'

/**
 * Format currency amount - matches Angular CurrencyPipe format
 * Currency: USD
 * Symbol: $
 * Decimal places: 2 (e.g., $1,234.56)
 * Negative: - $123.45
 * Thousands separator: $1,000.00
 * 
 * @param amount - Amount in dollars (number or string)
 * @param currency - Currency code (default: USD)
 * @param showCents - Whether to show cents (default: true)
 * @returns Formatted currency string (e.g., "$1,234.56" or "- $123.45")
 */
export function formatAmount(
  amount: number | string | null | undefined,
  _currency: string = 'USD',
  showCents: boolean = true
): string {
  // Handle null, undefined, or empty string
  if (amount === null || amount === undefined || amount === '') {
    return showCents ? '$0.00' : '$0'
  }

  // Convert to number if string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount

  // Handle invalid numbers
  if (isNaN(numAmount)) {
    return showCents ? '$0.00' : '$0'
  }

  // Handle zero
  if (numAmount === 0) {
    return showCents ? '$0.00' : '$0'
  }

  // Check if negative
  const isNegative = numAmount < 0
  const absAmount = Math.abs(numAmount)

  // Format with thousands separator and decimal places
  const formatted = absAmount.toLocaleString('en-US', {
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  })

  // Add dollar sign and handle negative (Angular format: - $123.45).
  // Use NBSP after the minus so "- $700.00" never wraps onto two lines in narrow columns.
  if (isNegative) {
    return `-\u00A0$${formatted}`
  }

  return `$${formatted}`
}

/**
 * Format currency for input fields - allows partial input while typing
 *
 * Rules:
 * - Max 6 digits before decimal point
 * - Max 2 digits after decimal point
 * - During typing: preserves partial decimals ("100.5" stays as "$100.5", "100." stays as "$100.")
 * - On blur (alwaysShowDecimals=true): pads to 2 decimals ("$100.50")
 *
 * @param value - Input value (string or number)
 * @param alwaysShowDecimals - If true, always show 2 decimals (use for onBlur and data loading)
 * @returns Formatted currency string for display in input
 */
export function formatCurrencyInput(
  value: string | number | null | undefined,
  alwaysShowDecimals?: boolean
): string {
  if (value === null || value === undefined || value === '') return ''

  const strValue = String(value)

  // Remove all characters except digits and the first dot
  const cleaned = strValue.replace(/[^0-9.]/g, '')
  if (!cleaned) return ''

  // Split on first dot only
  const firstDot = cleaned.indexOf('.')
  const hasDecimal = firstDot !== -1
  const integerRaw = hasDecimal ? cleaned.slice(0, firstDot) : cleaned
  // Remove any extra dots from decimal part
  const decimalRaw = hasDecimal ? cleaned.slice(firstDot + 1).replace(/\./g, '') : ''

  // Enforce limits: max 6 digits before decimal, max 2 after
  const integerLimited = integerRaw.slice(0, 6)
  const decimalLimited = decimalRaw.slice(0, 2)

  // Format integer part with thousands separator
  const integerNum = integerLimited === '' ? 0 : parseInt(integerLimited, 10)
  const formattedInteger = isNaN(integerNum) ? '0' : integerNum.toLocaleString('en-US')

  if (hasDecimal) {
    if (alwaysShowDecimals) {
      // On blur: pad to 2 decimals
      const paddedDecimal = decimalLimited.padEnd(2, '0')
      return `$${formattedInteger}.${paddedDecimal}`
    } else {
      // During typing: show decimal as the user typed it (no padding)
      return `$${formattedInteger}.${decimalLimited}`
    }
  } else {
    if (alwaysShowDecimals) {
      return `$${formattedInteger}.00`
    } else {
      return `$${formattedInteger}`
    }
  }
}

/**
 * Get numeric value from formatted currency string
 * Removes dollar sign, commas, and returns numeric value as string
 * 
 * @param value - Formatted currency string (e.g., "$1,234.56")
 * @returns Numeric value as string (e.g., "1234.56")
 */
export function getNumericCurrencyValue(value: string): string {
  if (!value) return ''

  // Remove dollar sign, commas, and spaces, but keep decimal point and trailing dot
  const numericValue = value.replace(/[^0-9.]/g, '')

  // Handle multiple decimal points - keep only the first one
  const firstDot = numericValue.indexOf('.')
  if (firstDot === -1) return numericValue
  // Keep everything up to and including the first dot, strip extra dots from the rest
  return numericValue.slice(0, firstDot + 1) + numericValue.slice(firstDot + 1).replace(/\./g, '')
}

/**
 * Format phone number
 * @param phone - Phone number string
 * @param format - Format type (default: 'US')
 * Formats as:
 * - 10 digits: (XXX) XXX-XXXX
 * - 12 digits: +XX(XXX) XXX-XXXX (e.g., +92(345) 678-7654)
 * - 13 digits: +XXX(XXX) XXX-XXXX (3-digit country code)
 */
export function formatPhone(phone: string, format: 'US' | 'raw' = 'US'): string {
  if (!phone) return ''

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')

  if (format === 'US') {
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    if (digits.length === 12) {
      return `+${digits.slice(0, 2)}(${digits.slice(2, 5)}) ${digits.slice(5, 8)}-${digits.slice(8)}`
    }
    if (digits.length === 13) {
      return `+${digits.slice(0, 3)}(${digits.slice(3, 6)}) ${digits.slice(6, 9)}-${digits.slice(9)}`
    }
  }

  return digits
}

/**
 * Filter phone input to allow only digits and auto-format
 * @param value - Input value
 * @returns Formatted phone number with only digits
 * Formats as:
 * - 10 digits: (XXX) XXX-XXXX
 * - 12 digits: +XX(XXX) XXX-XXXX (e.g., +92(345) 678-7654)
 * - 13 digits: +XXX(XXX) XXX-XXXX (3-digit country code)
 */
export function filterPhoneInput(value: string): string {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '')

  // Limit to 13 digits maximum
  const limitedDigits = digits.slice(0, 13)

  // Auto-format based on length
  if (limitedDigits.length === 0) return ''

  // For 13-digit numbers (with 3-digit country code) - Format: +XXX(XXX) XXX-XXXX
  if (limitedDigits.length === 13) {
    return `+${limitedDigits.slice(0, 3)}(${limitedDigits.slice(3, 6)}) ${limitedDigits.slice(6, 9)}-${limitedDigits.slice(9)}`
  }

  // For 12-digit numbers (with 2-digit country code) - Format: +XX(XXX) XXX-XXXX
  if (limitedDigits.length > 10) {
    if (limitedDigits.length <= 2) return `+${limitedDigits}`
    if (limitedDigits.length <= 5) return `+${limitedDigits.slice(0, 2)}(${limitedDigits.slice(2)}`
    if (limitedDigits.length <= 8) return `+${limitedDigits.slice(0, 2)}(${limitedDigits.slice(2, 5)}) ${limitedDigits.slice(5)}`
    return `+${limitedDigits.slice(0, 2)}(${limitedDigits.slice(2, 5)}) ${limitedDigits.slice(5, 8)}-${limitedDigits.slice(8)}`
  }

  // For 10-digit numbers (US format) - Format: (123) 456-7890
  if (limitedDigits.length <= 3) return `(${limitedDigits}`
  if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`
}

/**
 * Format date
 * @param date - Date string, Date object, or timestamp
 * @param formatString - Format string (default: 'MM/dd/yyyy')
 */
export function formatDate(
  date: string | Date | number,
  formatString: string = 'MM/dd/yyyy'
): string {
  try {
    let dateObj: Date

    if (typeof date === 'string') {
      const trimmed = date.trim()
      if (!trimmed) return ''
      dateObj = parseISO(trimmed)
      if (isNaN(dateObj.getTime())) {
        dateObj = new Date(trimmed.includes(' ') && !trimmed.includes('T') ? trimmed.replace(' ', 'T') : trimmed)
      }
      if (isNaN(dateObj.getTime())) {
        dateObj = new Date(trimmed)
      }
      if (isNaN(dateObj.getTime()) && !isNaN(Number(trimmed))) {
        dateObj = new Date(Number(trimmed))
      }
    } else if (typeof date === 'number') {
      dateObj = new Date(date)
    } else {
      dateObj = date
    }

    if (isNaN(dateObj.getTime())) {
      return typeof date === 'string' ? date : ''
    }

    return format(dateObj, formatString)
  } catch (error) {
    console.error('Error formatting date:', error)
    return typeof date === 'string' ? date : ''
  }
}

/**
 * Format date with time
 * @param date - Date string, Date object, or timestamp
 */
export function formatDateTime(
  date: string | Date | number,
  formatString: string = 'MM/dd/yyyy hh:mm a'
): string {
  return formatDate(date, formatString)
}

/** Parse `_MMDDYYYY_HHMMSS` timestamp embedded in policy declaration filenames (ms since epoch). */
export function parsePolicyDeclarationFilenameTimestamp(
  filename: string | null | undefined,
): number {
  if (!filename || typeof filename !== 'string') return 0

  const match = filename.match(/_(\d{8})_(\d{6})/)
  if (!match) return 0

  const [, date, time] = match
  const mm = Number(date.slice(0, 2))
  const dd = Number(date.slice(2, 4))
  const yyyy = Number(date.slice(4, 8))
  const hh = Number(time.slice(0, 2))
  const min = Number(time.slice(2, 4))
  const ss = Number(time.slice(4, 6))
  const parsed = new Date(yyyy, mm - 1, dd, hh, min, ss)
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime()
}

/** Access Confirmations list — newest file first (by embedded filename timestamp). */
export function sortPolicyDeclarationFilesNewestFirst<T extends { filename?: string }>(
  files: T[],
): T[] {
  return [...files].sort(
    (a, b) =>
      parsePolicyDeclarationFilenameTimestamp(b.filename) -
      parsePolicyDeclarationFilenameTimestamp(a.filename),
  )
}

/** Parse `_MMDDYYYY_HHMMSS` timestamp embedded in policy declaration filenames. */
export function formatTimestampFromPolicyDeclarationFilename(
  filename: string | null | undefined,
): string | null {
  if (!filename || typeof filename !== 'string') return null

  const match = filename.match(/_(\d{8})_(\d{6})/)
  if (!match) return null

  const [, date, time] = match
  const mm = date.slice(0, 2)
  const dd = date.slice(2, 4)
  const yyyy = date.slice(4, 8)
  const hh = time.slice(0, 2)
  const min = time.slice(2, 4)
  const ss = time.slice(4, 6)
  return `${mm}-${dd}-${yyyy} ${hh}:${min}:${ss}`
}

/**
 * Access Confirmations modal row labels from the embedded filename timestamp.
 * Example: `{ dateLabel: "July 13, 2026", timeLabel: "4:45 AM" }`
 */
export function formatAccessConfirmationLabelsFromFilename(
  filename: string | null | undefined,
): { dateLabel: string | null; timeLabel: string | null } {
  if (!filename || typeof filename !== 'string') {
    return { dateLabel: null, timeLabel: null }
  }
  const match = filename.match(/_(\d{8})_(\d{6})/)
  if (!match) return { dateLabel: null, timeLabel: null }

  const [, date, time] = match
  const mm = Number(date.slice(0, 2))
  const dd = Number(date.slice(2, 4))
  const yyyy = Number(date.slice(4, 8))
  const hh = Number(time.slice(0, 2))
  const min = Number(time.slice(2, 4))
  const parsed = new Date(yyyy, mm - 1, dd, hh, min)
  if (Number.isNaN(parsed.getTime())) return { dateLabel: null, timeLabel: null }

  return {
    dateLabel: parsed.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }),
    timeLabel: parsed.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  }
}

